import { InAppState } from "../base/InAppState";
import * as echarts from 'echarts';
import template from './IndexInAppUser.html';

export class IndexState extends InAppState
{
    static ROUTE = '/';
    static ID = 'index';

    async enter()
    {
        this.app.view.render(
            this.app.view.pageContent,
            template
        );

        this._initDocumentsChart();
        this._loadChartData();

        this._loadStats();
    }

    async _loadStats()
    {
        const creditLimit = this.app.organisation?.premium?.numDocumentGenerationPerMonth || 0;

        const { result } = await this.app.api.get( '/organisation/stats' );

        if ( result.status !== 200 || !result.data )
        {
            return;
        }

        const { numTemplates, numPacks, numGeneratedDocuments, numCreditUsed } = result.data;
        const pct = creditLimit > 0 ? Math.round( ( numCreditUsed / creditLimit ) * 100 ) : 0;

        this._animateNumber( 'stat-templates', numTemplates );
        this._animateNumber( 'stat-packs', numPacks );
        this._animateNumber( 'stat-documents', numGeneratedDocuments );
        this._animateNumber( 'stat-credits', numCreditUsed, ( v ) => `${v.toLocaleString()} / ${creditLimit.toLocaleString()}` );

        const badgeEl = document.getElementById( 'stat-credits-badge' );
        if ( badgeEl ) badgeEl.textContent = `${pct}%`;
    }

    _animateNumber( elementId, target, formatter = null )
    {
        const el = document.getElementById( elementId );
        if ( !el ) return;

        const duration = 1000;
        const steps = 40;
        const stepTime = duration / steps;
        let current = 0;
        let step = 0;

        const format = formatter || ( ( v ) => v.toLocaleString() );

        const interval = setInterval( () =>
        {
            step++;
            // Ease-out quad
            const t = step / steps;
            const eased = t * ( 2 - t );
            current = Math.round( eased * target );

            el.textContent = format( current );

            if ( step >= steps )
            {
                el.textContent = format( target );
                clearInterval( interval );
            }
        }, stepTime );
    }

    _initDocumentsChart()
    {
        const chartEl = document.querySelector( '#documents-chart' );
        if ( !chartEl ) return;

        this._chart = echarts.init( chartEl );

        const styles = getComputedStyle( document.documentElement );
        const successColor = styles.getPropertyValue( '--bs-success' ).trim();
        const dangerColor = styles.getPropertyValue( '--bs-danger' ).trim();

        const months = [ 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec' ];
        const emptyData = new Array( 12 ).fill( 0 );

        const options = {
            tooltip: {
                trigger: 'axis',
            },
            legend: {
                show: false,
            },
            grid: {
                left: 20,
                right: 20,
                top: 40,
                bottom: 0,
                containLabel: true,
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: months,
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: {
                    color: '#adb0bb',
                },
            },
            yAxis: {
                type: 'value',
                name: 'Documents',
                minInterval: 1,
                nameTextStyle: {
                    color: '#adb0bb',
                },
                axisLabel: {
                    color: '#adb0bb',
                },
                splitLine: {
                    lineStyle: {
                        color: 'rgba(173, 176, 187, 0.15)',
                    },
                },
            },
            series: [
                {
                    name: 'Success',
                    type: 'line',
                    smooth: true,
                    symbol: 'none',
                    data: emptyData,
                    lineStyle: { width: 2 },
                    itemStyle: { color: successColor },
                    areaStyle: {
                        opacity: 0.25,
                    },
                },
                {
                    name: 'Errors',
                    type: 'line',
                    smooth: true,
                    symbol: 'none',
                    data: emptyData,
                    lineStyle: { width: 2 },
                    itemStyle: { color: dangerColor },
                    areaStyle: {
                        opacity: 0.15,
                    },
                },
            ],
        };

        this._chart.setOption( options );

        this._resizeHandler = () => this._chart?.resize();
        window.addEventListener( 'resize', this._resizeHandler );
    }

    async _loadChartData()
    {
        const { result } = await this.app.api.get( '/logs/document-generation' );

        if ( result.status !== 200 || !Array.isArray( result.data ) )
        {
            return;
        }

        const now = new Date();
        const currentYear = now.getFullYear();
        const success = new Array( 12 ).fill( 0 );
        const errors = new Array( 12 ).fill( 0 );

        for ( const log of result.data )
        {
            const date = new Date( log.createdAt );
            if ( date.getFullYear() !== currentYear ) continue;

            const month = date.getMonth();
            if ( log.status === 'success' )
            {
                success[ month ]++;
            }
            else
            {
                errors[ month ]++;
            }
        }

        if ( this._chart )
        {
            this._chart.setOption( {
                series: [
                    { data: success },
                    { data: errors },
                ],
            } );
        }
    }

    async exit()
    {
        if ( this._chart )
        {
            window.removeEventListener( 'resize', this._resizeHandler );
            this._chart.dispose();
            this._chart = null;
        }

        await super.exit();
    }

    getContentHeaderData()
    {
        return {
            title: 'Dashboard',
            breadcrumb: [ 'Home' ]
        };
    }
}
